import { useApolloClient, useSubscription } from '@apollo/client/react';
import {
  BroadcastOperation,
  type CompletePreBookingFragment,
  GetStorePreBookingsDocument,
  type GetStorePreBookingsQuery,
  PreBookingUpdateDocument,
  type PreBookingUpdateFragment,
} from '@drycut/graphql';
import { useEffect } from 'react';

type Props = {
  callback?: (broadcast: PreBookingUpdateFragment) => void;
  slug: string;
};

export function usePreBookingsPubSub({ slug, callback }: Props) {
  const client = useApolloClient();

  const { data } = useSubscription(PreBookingUpdateDocument, { variables: { slug } });
  const broadcast: PreBookingUpdateFragment | undefined = data?.preBookingUpdate;

  useEffect(() => {
    if (broadcast) {
      const previous = client.readQuery<GetStorePreBookingsQuery>({
        query: GetStorePreBookingsDocument,
      });

      const previousNodes = (previous?.storePreBookings?.nodes ||
        []) as CompletePreBookingFragment[];

      switch (broadcast.operation) {
        case BroadcastOperation.Add: {
          // The service is already in the array, so we don't need to add it again.
          if (previousNodes.some((node) => node.id === broadcast.preBooking.id)) {
            return;
          }

          const nodes = [...previousNodes, broadcast.preBooking];

          client.writeQuery({
            query: GetStorePreBookingsDocument,
            data: {
              ...previous,
              storePreBookings: { ...previous?.storePreBookings, nodes },
            },
          });
          break;
        }
        case BroadcastOperation.Remove: {
          client.cache.evict({ id: `PreBooking:${broadcast.preBooking.id}` });
          client.cache.gc();

          break;
        }
        case BroadcastOperation.Update: {
          // For updates we don't need to do anything since Apollo will update the cache
          // automatically.
          break;
        }
      }

      // The callback is intentionally an imperative side effect.
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-pass-data-to-parent
      callback?.(broadcast);
    }
  }, [broadcast, client, callback]);
}
